import {
  EmptyState,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  useLoading,
  useModal,
  useMoment,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { ONLY_DATE_FORMAT } from '../../../constants/app';
import { ASSET_TYPE_VEHICLE } from '../../../constants/asset';
import { TABLE_COLUMNS_NAME } from '../../../constants/fleetAssets';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { apiParamsState } from '../../../storage/app';
import { FleetAsset, FleetJobType } from '../../../types/fleet';
import { isBrand, isSubBrand } from '../../../utils/currentStates';
import { getFormattedNumber } from '../../../utils/numbers';
import VehicleIcon from '../../map/markers/VehicleIcon';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import { StyledTooltip } from '../../ui/StyledTooltip';
import { useFleetAssets } from '../hooks/useFleetAssets';
import FleetServiceJobModal from '../services/jobs/FleetServiceJobModal';
import FleetAssetDocuments from './FleetAssetDocuments';
import FleetAssetModal from './FleetAssetModal';
import FleetAssetRefuelModal from './FleetAssetRefuelModal';

export function FleetAssetList() {
  const { theme, css, classes } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [assets, setAssets] = useState<FleetAsset[]>();
  const {
    page,
    onChangePage,
    loadFleetAssets,
    reload,
    search,
    filters: { brandId, assetTypes, subBrandId },
    onReload,
  } = useFleetAssets();
  const [totalItems, setTotalItems] = useState(0);
  const { formatDateFromTs } = useMoment();
  const { loading, stopLoading, startLoading } = useLoading();
  const { showFailFeedback } = useFeedback();
  const [selectedAsset, setSelectedAsset] = useState<FleetAsset>();
  const { openModal, closeModal, showModal } = useModal();
  const {
    openModal: openServiceJobModal,
    closeModal: closeServiceJobModal,
    showModal: showServiceJobModal,
  } = useModal();
  const { openModal: openRefuelModal, closeModal: closeRefuelModal, showModal: showRefuelModal } = useModal();

  useUpdateEffect(() => {
    onChangePage(0);
  }, [search, reload, brandId, assetTypes, subBrandId, apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (page === 0) {
      onChangePage(1);
      return;
    }
    const loadData = async () => {
      startLoading();
      await loadAssets();
      stopLoading();
    };

    loadData();
  }, [page]);

  const loadAssets = async () => {
    const { assets, error, total } = await loadFleetAssets();
    if (error) {
      showFailFeedback(error);
      setAssets(undefined);
      setTotalItems(0);
    } else {
      setAssets(assets);
      setTotalItems(total ?? 0);
    }
  };

  const onOpenDetailModal = (asset: FleetAsset) => {
    setSelectedAsset(asset);
    openModal();
  };

  const onCloseDetailModal = () => {
    closeModal();
    setSelectedAsset(undefined);
  };

  const onOpenServiceJobModal = (asset: FleetAsset) => {
    setSelectedAsset(asset);
    openServiceJobModal();
  };

  const onCloseServiceJobModal = () => {
    closeServiceJobModal();
    setSelectedAsset(undefined);
  };

  const onOpenRefuelModal = (asset: FleetAsset) => {
    setSelectedAsset(asset);
    openRefuelModal();
  };

  const onCloseRefuelModal = () => {
    closeRefuelModal();
    setSelectedAsset(undefined);
  };

  const formatOnlyDateFromTs = (ts: number) => {
    return formatDateFromTs(ts, ONLY_DATE_FORMAT);
  };

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
      {!assets && !loading && <EmptyState title="Todavía no cuentas con activos." />}
      {!assets && loading && <Loading />}
      {assets && (
        <>
          {!!assets?.length && (
            <StyledPaginatedTable
              columns={TABLE_COLUMNS_NAME}
              data={assets.map((asset) => {
                const withDocuments = asset.assetType === ASSET_TYPE_VEHICLE;

                return {
                  items: [
                    asset.label || '-',
                    asset.vehicle?.vin || '-',
                    isBrand(asset.vehicle?.brand) ? asset.vehicle.brand.name : asset.vehicle?.brand || '-',
                    isSubBrand(asset.vehicle?.subBrand) ? asset.vehicle.subBrand.name : asset.vehicle?.subBrand || '-',
                    asset.vehicle?.licensePlate || asset?.container?.identifier || '-',
                    <VehicleIcon
                      key={`icon-${asset._id}`}
                      assetType={asset.assetType}
                      color={asset.vehicle?.color}
                      vehicleType={asset.vehicle?.vehicleType}
                      iconSize={30}
                    />,
                    getFormattedNumber(asset.odometerState?.calculatedOdometer, { suffix: 'km' }),
                    getFormattedNumber(asset.vehicle?.marketValue, { prefix: '$', trimmed: true }),
                    withDocuments ? (
                      <FleetAssetDocuments
                        key={`documents-${asset._id}`}
                        documents={asset.vehicle?.documents}
                        asset={asset}
                      />
                    ) : (
                      '-'
                    ),
                    asset.lastMaintenance ? formatOnlyDateFromTs(asset.lastMaintenance.date) : '-',
                    asset.lastReparation ? formatOnlyDateFromTs(asset.lastReparation.date) : '-',
                    <FlexRow
                      gap={theme.sizing.scale200}
                      key={`details-${asset._id}`}
                    >
                      <StyledTooltip
                        content="Carga de combustible"
                        showArrow={false}
                      >
                        <Button
                          size="mini"
                          shape="square"
                          onClick={() => onOpenRefuelModal(asset)}
                          kind="tertiary"
                        >
                          <MaterialIcon
                            name="local_gas_station"
                            size={theme.sizing.scale700}
                          />
                        </Button>
                      </StyledTooltip>
                      <StyledTooltip
                        content="Registrar reparación"
                        showArrow={false}
                      >
                        <Button
                          size="mini"
                          shape="square"
                          onClick={() => onOpenServiceJobModal(asset)}
                          kind="tertiary"
                        >
                          <MaterialIcon
                            name="car_repair"
                            size={theme.sizing.scale700}
                          />
                        </Button>
                      </StyledTooltip>
                      <StyledTooltip
                        key={`edit-${asset._id}`}
                        content="Editar"
                        showArrow={false}
                      >
                        <Button
                          size="mini"
                          shape="square"
                          onClick={() => onOpenDetailModal(asset)}
                          kind="tertiary"
                        >
                          <MaterialIcon
                            name="edit"
                            size={theme.sizing.scale700}
                          />
                        </Button>
                      </StyledTooltip>
                    </FlexRow>,
                  ],
                };
              })}
              totalItems={totalItems}
              page={page}
              onPageChange={onChangePage}
              loading={loading}
            />
          )}
          {!assets.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
      {selectedAsset && (
        <FleetAssetModal
          isOpen={showModal}
          onClose={(update) => {
            if (update) onReload();
            onCloseDetailModal();
          }}
          selectedAsset={selectedAsset}
        />
      )}
      <FleetServiceJobModal
        selectedAsset={selectedAsset}
        jobType={FleetJobType.repair}
        isOpen={showServiceJobModal}
        onClose={(update) => {
          if (update) onReload();
          onCloseServiceJobModal();
        }}
      />
      {selectedAsset && (
        <FleetAssetRefuelModal
          selectedAsset={selectedAsset}
          isOpen={showRefuelModal}
          onClose={(update) => {
            if (update) onReload();
            onCloseRefuelModal();
          }}
        />
      )}
    </FlexColumn>
  );
}
